import { useState } from 'react';
import type { ComboboxItem } from '@screentone/core';
import {
  Button,
  Checkbox,
  Dialog,
  Divider,
  FormHelperText,
  FormLabel,
  Group,
  Input,
  MultiSelect,
  Select,
  Table,
  Token,
  Typography,
  useModalPortal,
  Wrapper
} from '@screentone/core';

import { StructureGroup, StructureGroupConfig, TreatmentTypeLimit } from 'data/generated/graphql';
import { resolveComboboxUpdate, stringsToComboboxItems } from 'utils/combobox';
import { FlatSlot } from 'utils/layoutUtils';
import styles from './GroupOptionsModal.module.scss';

// Groups default to disabled (starts off) unless explicitly set otherwise.
const DEFAULT_TOGGLE_DEFAULT_VALUE = false;

interface GroupOptionsModalProps {
  group: StructureGroup;
  availableTreatmentTypes: string[];
  modules: FlatSlot[];
  onSave: (patch: Partial<StructureGroupConfig>) => void;
  onDismiss: () => void;
}
export const GroupOptionsModal = ({
  group,
  availableTreatmentTypes,
  modules,
  onSave,
  onDismiss
}: GroupOptionsModalProps) => {
  const { renderNode } = useModalPortal();
  const { config } = group;

  const [label, setLabel] = useState(config.label);
  const [min, setMin] = useState(config.min != null ? String(config.min) : '');
  const [max, setMax] = useState(config.max != null ? String(config.max) : '');
  const [showModuleCount, setShowModuleCount] = useState(config.showModuleCount === true);
  const [softLimit, setSoftLimit] = useState(config.softLimit != null ? String(config.softLimit) : '');
  const [softLimitMessage, setSoftLimitMessage] = useState(config.softLimitMessage ?? '');
  const [allowedTTs, setAllowedTTs] = useState<ComboboxItem[]>(stringsToComboboxItems(config.allowedTreatmentTypes));
  const [defaultTT, setDefaultTT] = useState(config.defaultTreatmentType ?? '');
  const [canToggle, setCanToggle] = useState(config.canToggle === true);
  const [defaultValue, setDefaultValue] = useState(config.defaultValue ?? DEFAULT_TOGGLE_DEFAULT_VALUE);
  const [isRightRailToggleable, setIsRightRailToggleable] = useState(config.isRightRailToggleable === true);
  const [draggableTTs, setDraggableTTs] = useState<ComboboxItem[]>(
    stringsToComboboxItems(config.draggableTreatmentTypes ?? [])
  );
  const [treatmentTypeLimits, setTreatmentTypeLimits] = useState<TreatmentTypeLimit[]>(
    config.treatmentTypeLimits ?? []
  );
  const [limitsError, setLimitsError] = useState<string | null>(null);

  const ttOptions = stringsToComboboxItems([...availableTreatmentTypes].sort());

  const presentTTs = [...new Set(modules.map((m) => m.ttKey).filter((tt): tt is string => Boolean(tt)))];

  // TTs in allowedTTs that are at or above their per-type max — page editors can't actually add them
  const ineffectiveAllowedTTs = allowedTTs
    .map((i) => String(i.value))
    .filter((tt) => {
      const limit = treatmentTypeLimits.find((l) => l.ref === tt);
      if (limit?.max == null) return false;
      const currentCount = modules.filter((m) => m.ttKey === tt).length;
      return currentCount >= limit.max;
    });

  const handleAllowedTTChange = (items: ComboboxItem[] | ((prev: ComboboxItem[]) => ComboboxItem[])) => {
    const resolved = resolveComboboxUpdate(items, allowedTTs);
    setAllowedTTs(resolved);
    if (defaultTT && !resolved.some((i) => i.value === defaultTT)) {
      setDefaultTT('');
    }
  };

  const handleTreatmentLimitChange = (ref: string, field: 'min' | 'max', value: string) => {
    if (limitsError) setLimitsError(null);
    setTreatmentTypeLimits((prev) => {
      const existing = prev.find((l) => l.ref === ref);
      const num = value !== '' ? Number(value) : undefined;
      if (existing) {
        return prev.map((l) => (l.ref === ref ? { ...l, [field]: num } : l));
      }
      return [...prev, { ref, [field]: num }];
    });
  };

  const handleSave = () => {
    const invalidLimits = treatmentTypeLimits.filter((l) => l.min != null && l.max != null && l.min > l.max);
    if (invalidLimits.length > 0) {
      setLimitsError(`Min must be less than or equal to max for: ${invalidLimits.map((l) => l.ref).join(', ')}`);
      return;
    }
    setLimitsError(null);

    const cleanedLimits = treatmentTypeLimits.filter((l) => l.min != null || l.max != null);
    onSave({
      label: label.trim() || config.label,
      min: min !== '' ? Number(min) : undefined,
      max: max !== '' ? Number(max) : undefined,
      showModuleCount,
      softLimit: softLimit !== '' ? Number(softLimit) : undefined,
      softLimitMessage: softLimitMessage.trim() || undefined,
      allowedTreatmentTypes: allowedTTs.map((i) => String(i.value)),
      defaultTreatmentType: defaultTT || undefined,
      canToggle,
      defaultValue: canToggle ? defaultValue : undefined,
      isRightRailToggleable,
      draggableTreatmentTypes: draggableTTs.length > 0 ? draggableTTs.map((i) => String(i.value)) : undefined,
      treatmentTypeLimits: cleanedLimits.length > 0 ? cleanedLimits : undefined
    });
  };

  return (
    <Dialog status="open" onDismiss={onDismiss} focusTrap={false} renderNode={renderNode} size="lg">
      <Dialog.Title>Group Options</Dialog.Title>
      <Dialog.Content>
        <Wrapper padding={{ all: 'md' }}>
          <Group gap="md" direction="column" fullWidth>
            <Group.Item fullWidth>
              <FormHelperText>
                <Token>tokens</Token> show the field name in the data structure.
              </FormHelperText>
            </Group.Item>

            <Group.Item fullWidth>
              <Group gap="xs" direction="column" fullWidth>
                <FormLabel label="Label" labelPosition="top" fullWidth />
                <Input
                  type="text"
                  value={label}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setLabel(e.target.value)}
                />
                <FormHelperText>
                  <Token>label</Token>
                </FormHelperText>
              </Group>
            </Group.Item>

            <Group.Item fullWidth>
              <Group gap="xs" direction="column" fullWidth>
                <FormLabel label="Group ID" labelPosition="top" fullWidth />
                <Input type="text" value={group.id} disabled />
                <FormHelperText>
                  <Token>id</Token>
                </FormHelperText>
              </Group>
            </Group.Item>

            <Divider />

            <Group.Item fullWidth>
              <Typography variant="label2" weight="bold" margin={{ bottom: 'none' }}>
                Treatment Types
              </Typography>
            </Group.Item>

            <Group.Item fullWidth>
              <Group gap="xs" direction="column" fullWidth>
                <FormLabel label="Allowed treatment types" labelPosition="top" fullWidth />
                <MultiSelect
                  items={ttOptions}
                  selectedItems={allowedTTs}
                  onSelectedItemsChange={handleAllowedTTChange}
                  status="ready"
                  emptyContent={<Typography variant="note">No treatment types available</Typography>}
                />
                <FormHelperText>
                  When editors add modules to this group on a page, only these treatment types will be offered. If
                  empty, no treatment types can be added to this group. <Token>allowedTreatmentTypes</Token>
                </FormHelperText>
                {ineffectiveAllowedTTs.length > 0 && (
                  <FormHelperText>
                    Note: {ineffectiveAllowedTTs.join(', ')} {ineffectiveAllowedTTs.length === 1 ? 'is' : 'are'} at
                    their per-type limit — page editors cannot add more of{' '}
                    {ineffectiveAllowedTTs.length === 1 ? 'it' : 'them'} even though{' '}
                    {ineffectiveAllowedTTs.length === 1 ? 'it is' : 'they are'} in the allowed list.
                  </FormHelperText>
                )}
              </Group>
            </Group.Item>

            {allowedTTs.length > 0 && (
              <Group.Item fullWidth>
                <Group gap="xs" direction="column" fullWidth>
                  <FormLabel label="Default treatment type" labelPosition="top" fullWidth />
                  {/* When a toggleable group is switched on, a new empty module is created using this
                      treatment type (falls back to the first allowedTreatmentType if not set).
                      Note: the group's previous modules are not restored on re-enable. */}
                  <Select
                    value={defaultTT}
                    onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setDefaultTT(e.target.value)}
                  >
                    <option value="">— none —</option>
                    {allowedTTs.map((i) => (
                      <option key={String(i.value)} value={String(i.value)}>
                        {String(i.label)}
                      </option>
                    ))}
                  </Select>
                  <FormHelperText>
                    Used when toggling the group on. <Token>defaultTreatmentType</Token>
                  </FormHelperText>
                </Group>
              </Group.Item>
            )}

            <Divider />

            <Group.Item fullWidth>
              <Typography variant="label2" weight="bold" margin={{ bottom: 'none' }}>
                Module Limits
              </Typography>
            </Group.Item>

            <Group.Item fullWidth>
              <Group gap="md" fullWidth>
                <Group.Item>
                  <Group gap="xs" direction="column" fullWidth>
                    <FormLabel label="Min modules" labelPosition="top" fullWidth />
                    <Input
                      type="number"
                      min={0}
                      value={min}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => setMin(e.target.value)}
                      placeholder="—"
                    />
                    <FormHelperText>
                      <Token>min</Token>
                    </FormHelperText>
                  </Group>
                </Group.Item>
                <Group.Item>
                  <Group gap="xs" direction="column" fullWidth>
                    <FormLabel label="Max modules" labelPosition="top" fullWidth />
                    <Input
                      type="number"
                      min={0}
                      value={max}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => setMax(e.target.value)}
                      placeholder="—"
                    />
                    <FormHelperText>
                      <Token>max</Token>
                    </FormHelperText>
                  </Group>
                </Group.Item>
              </Group>
            </Group.Item>

            <Group.Item fullWidth>
              <Group gap="xs" direction="column" fullWidth>
                <FormLabel label="Soft limit" labelPosition="top" fullWidth />
                <Input
                  type="number"
                  min={0}
                  value={softLimit}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSoftLimit(e.target.value)}
                  placeholder="—"
                />
                <FormHelperText>
                  Triggers a warning on publish if exceeded. <Token>softLimit</Token>
                </FormHelperText>
              </Group>
            </Group.Item>

            {softLimit !== '' && (
              <Group.Item fullWidth>
                <Group gap="xs" direction="column" fullWidth>
                  <FormLabel label="Soft limit message" labelPosition="top" fullWidth />
                  <Input
                    type="text"
                    value={softLimitMessage}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSoftLimitMessage(e.target.value)}
                    placeholder="Optional warning message"
                  />
                  <FormHelperText>
                    <Token>softLimitMessage</Token>
                  </FormHelperText>
                </Group>
              </Group.Item>
            )}

            <Divider />

            <Group.Item fullWidth>
              <Typography variant="label2" weight="bold" margin={{ bottom: 'none' }}>
                Behavior
              </Typography>
            </Group.Item>

            <Group.Item fullWidth>
              <FormLabel label="Show module count" labelPosition="right" fullWidth>
                <Checkbox checked={showModuleCount} onChange={() => setShowModuleCount((v) => !v)} />
              </FormLabel>
              <FormHelperText>
                <Token>showModuleCount</Token>
              </FormHelperText>
            </Group.Item>

            <Group.Item fullWidth>
              <Group gap="xs" direction="column" fullWidth>
                <FormLabel label="Can be toggled on/off by editors" labelPosition="right" fullWidth>
                  <Checkbox checked={canToggle} onChange={() => setCanToggle((v) => !v)} />
                </FormLabel>
                <FormHelperText>
                  When enabled, editors see an Enable/disable switch on this group in the page editor.{' '}
                  <Token>canToggle</Token>
                </FormHelperText>
              </Group>
            </Group.Item>

            <Group.Item fullWidth>
              <Group gap="xs" direction="column" fullWidth>
                <FormLabel label="Starts enabled on new pages" labelPosition="right" fullWidth>
                  <Checkbox
                    checked={canToggle && defaultValue}
                    disabled={!canToggle}
                    onChange={() => setDefaultValue((v) => !v)}
                  />
                </FormLabel>
                <FormHelperText>
                  When unchecked, the group&apos;s toggle starts off on every new page &mdash; editors must turn it on
                  manually. Only applies when &ldquo;Can be toggled&rdquo; is on. <Token>defaultValue</Token>{' '}
                  (initializes <Token>isGroupEnabled</Token> on flat modules)
                </FormHelperText>
              </Group>
            </Group.Item>

            <Group.Item fullWidth>
              <Group gap="xs" direction="column" fullWidth>
                <FormLabel label="Right rail toggleable" labelPosition="right" fullWidth>
                  <Checkbox checked={isRightRailToggleable} onChange={() => setIsRightRailToggleable((v) => !v)} />
                </FormLabel>
                <FormHelperText>
                  Shows this group as an on/off toggle in the Page Settings panel&apos;s Module Enablement section.{' '}
                  <Token>isRightRailToggleable</Token>
                </FormHelperText>
              </Group>
            </Group.Item>

            <Divider />

            <Group.Item fullWidth>
              <Typography variant="label2" weight="bold" margin={{ bottom: 'none' }}>
                Drag &amp; Drop
              </Typography>
            </Group.Item>

            <Group.Item fullWidth>
              <Group gap="xs" direction="column" fullWidth>
                <FormLabel label="Draggable treatment types" labelPosition="top" fullWidth />
                <MultiSelect
                  items={ttOptions}
                  selectedItems={draggableTTs}
                  onSelectedItemsChange={(items: ComboboxItem[] | ((prev: ComboboxItem[]) => ComboboxItem[])) =>
                    setDraggableTTs(resolveComboboxUpdate(items, draggableTTs))
                  }
                  status="ready"
                  emptyContent={<Typography variant="note">No treatment types available</Typography>}
                />
                <FormHelperText>
                  Modules of these treatment types can be reordered via drag-and-drop in the page editor. Leave empty to
                  disable dragging for all modules in this group. <Token>draggableTreatmentTypes</Token>
                </FormHelperText>
              </Group>
            </Group.Item>

            <>
              <Divider />

              <Group.Item fullWidth>
                <Typography variant="label2" weight="bold" margin={{ bottom: 'none' }}>
                  Per-treatment-type limits
                </Typography>
              </Group.Item>

              <Group.Item fullWidth>
                <FormHelperText>
                  Override min/max slot counts for individual treatment types within this group.{' '}
                  <Token>treatmentTypeLimits</Token>
                </FormHelperText>
              </Group.Item>

              {presentTTs.length === 0 && (
                <Group.Item fullWidth>
                  <Typography variant="note" color="asphalt">
                    No modules have been added to this group yet. Add modules to configure per-treatment-type limits.
                  </Typography>
                </Group.Item>
              )}
              {presentTTs.length > 0 && (
                <Group.Item fullWidth>
                  <div className={styles.tableWrapper}>
                    <Table fullWidth>
                      <Table.Head>
                        <Table.Row>
                          <Table.Cell isLabel>Treatment type</Table.Cell>
                          <Table.Cell isLabel className={styles.numericCell}>
                            Min
                          </Table.Cell>
                          <Table.Cell isLabel className={styles.numericCell}>
                            Max
                          </Table.Cell>
                        </Table.Row>
                      </Table.Head>
                      <Table.Body>
                        {presentTTs.map((tt) => {
                          const existing = treatmentTypeLimits.find((l) => l.ref === tt);
                          return (
                            <Table.Row key={tt}>
                              <Table.Cell>
                                <Typography variant="note" margin={{ bottom: 'none' }}>
                                  {tt}
                                </Typography>
                              </Table.Cell>
                              <Table.Cell className={styles.numericCell}>
                                <Input
                                  type="number"
                                  min={0}
                                  value={existing?.min != null ? String(existing.min) : ''}
                                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                    handleTreatmentLimitChange(tt, 'min', e.target.value)
                                  }
                                  placeholder="—"
                                />
                              </Table.Cell>
                              <Table.Cell className={styles.numericCell}>
                                <Input
                                  type="number"
                                  min={0}
                                  value={existing?.max != null ? String(existing.max) : ''}
                                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                    handleTreatmentLimitChange(tt, 'max', e.target.value)
                                  }
                                  placeholder="—"
                                />
                              </Table.Cell>
                            </Table.Row>
                          );
                        })}
                      </Table.Body>
                    </Table>
                  </div>
                </Group.Item>
              )}
              {limitsError && (
                <Group.Item fullWidth>
                  <Typography variant="note" color="lava">
                    {limitsError}
                  </Typography>
                </Group.Item>
              )}
            </>
          </Group>
        </Wrapper>
        <Divider />
        <Wrapper padding={{ all: 'md' }}>
          <Group gap="sm" justify="flex-end">
            <Button onClick={onDismiss} secondary>
              Cancel
            </Button>
            <Button onClick={handleSave} primary>
              Save
            </Button>
          </Group>
        </Wrapper>
      </Dialog.Content>
    </Dialog>
  );
};
